import React, { useState, useRef, useEffect } from "react"
import { trans } from "../../../../_providers/Translation"
import loader from "../../../../assets/img/loader.svg"
import removeImage from "@/assets/img/reservations/icons/remove.svg"
import uploadImage from "@/assets/img/reservations/icons/upload.svg"
import deleteIcon from "@/assets/img/reservations/icons/delete.svg"

// libraries
import { Button } from "@mui/material"
import moment from "moment"
import Question from "@/components/modals/messages/Question"
import Api from "@/_helpers/Api"

export default function AppliancesFieldDevice({
  appliance,
  index,
  totalCount,
  task,
  loading,
  isChild,
  canModify,
  canModifyAppliance,
  onUpdateField,
  onRemove,
  onSelectDevice,
  onSelectBrand,
  onModelChange,
  isExpanded,
  onToggle,
  hasError,
  validations = {},
}) {
  console.log(appliance)
  const T = task || {}
  const ap = appliance
  const idx = index

  const modalQuestionRef = useRef(null)

  const hidden = !isExpanded
  const v = field => validations[`appliances.${idx}.${field}`] ? 'validate' : ''
  const [images, setImages] = useState([])
  const [imageSizeError, setImageSizeError] = useState(null)
  const [brandOptions, setBrandOptions] = useState([])
  const fileInputRef = useRef(null)
  const hiddenFileInputRef = useRef(null)

  useEffect(() => {
    if ((appliance?.images || []).length) {
      setImages(appliance.images)
    }
  }, [appliance?.images])

  useEffect(() => {
    const categoryId = ap.appliancecategory?.id
    if (categoryId) {
      Api.get('partners/brands/all', {
        params: { category_id: Number(categoryId) }
      }).then(res => {
        const items = Array.isArray(res.data) ? res.data : (res.data.items || [])
        setBrandOptions(items)
      }).catch(() => setBrandOptions([]))
    } else {
      setBrandOptions([])
    }
  }, [ap.appliancecategory?.id])


  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files)
    const maxSize = 1024 * 1024 // 1024 KB

    const oversized = files.filter(f => f.size > maxSize)
    const validFiles = files.filter(f => f.size <= maxSize)

    if (oversized.length) {
      setImageSizeError(trans('messages.imageSizeExceeded', { files: oversized.map(f => f.name).join(', ') }))
    } else {
      setImageSizeError(null)
    }

    validFiles.forEach((file) => {
      const reader = new FileReader()
      reader.onload = (event) => {
        setImages((prev) => [
          ...prev,
          {
            id: Math.random().toString(36).slice(2, 11),
            name: file.name,
            file: file,
            preview: event.target.result,
          },
        ])
      }
      reader.readAsDataURL(file)
    })

    // Update the hidden file input with all files using DataTransfer
    updateHiddenFileInput([...images.map((img) => img.file), ...validFiles])

    // Reset input
    e.target.value = ""
  }

  const updateHiddenFileInput = (filesList) => {
    if (!hiddenFileInputRef.current) return

    const dataTransfer = new DataTransfer()
    filesList.forEach((item) => {
      // Only add if it's an actual File object (not a server image)
      if (item instanceof File) {
        dataTransfer.items.add(item)
      }
    })
    hiddenFileInputRef.current.files = dataTransfer.files
  }

  const handleDeleteImage = (imageId) => {
    let modal = modalQuestionRef.current

    modal.open(imageId)

    modal.onSuccess(() => {
      handleRemoveImage(imageId)
    })
  }

  const handleRemoveImage = (imageId) => {
    const updatedImages = images.filter((img) => img.id !== imageId)
    setImages(updatedImages)

    // Update the hidden file input without the removed file
    updateHiddenFileInput(updatedImages.map((img) => img.file))

    Api.delete(`/reservations/appliance-image/${imageId}`)
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className="device-container">
      <Question
        ref={modalQuestionRef}
        mainMessage={trans("headings.confirmDelete")}
        secondaryMessage={trans("subheadings.confirmDelete")}
      />

      {ap.id ? (
        <input type="hidden" name={`appliances[${idx}][id]`} value={ap.id} />
      ) : (
        <></>
      )}
      <div
        className={`head ${hidden ? "collapsed" : ""} ${hasError && hidden ? "validate" : ""}`}
        onClick={onToggle}
      >
        {`${trans('labels.device')} ${idx ? "(" : ""}${idx ? idx + 1 : ""}${idx ? ")" : ""}`}
        {totalCount > 1 && onRemove && (
          <Button
            onClick={(e) => {
              e.stopPropagation()
              onRemove(idx)
            }}
          >
            <img src={removeImage} alt="Remove" />
          </Button>
        )}
      </div>

      <div className="body" style={hidden ? { display: 'none' } : {}}>
          <div className="row">
            <div className="col">
              <div className="custom-input">
                <label>{trans("labels.category")}*</label>
                <select
                  className={v('appliance_category_id')}
                  name={`appliances[${idx}][appliance_category_id]`}
                  value={
                    ap.appliancecategory && ap.appliancecategory.id
                      ? ap.appliancecategory.id
                      : ""
                  }
                  onChange={(e) => onSelectDevice(idx, e)}
                  disabled={isChild || !canModify || !canModifyAppliance}
                >
                  <option value="">{trans("random.select")}</option>
                  {(T.appliances?.categories || []).map((category) => (
                    <option key={"c-" + category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col">
              <div className="custom-input">
                <label>{trans("labels.make")}</label>
                <select
                  name={`appliances[${idx}][appliance_brand_id]`}
                  value={ap.appliancebrand?.id || ""}
                  onChange={(e) => {
                    const id = e.target.value
                    const found = brandOptions.find(b => String(b.id) === String(id))
                    onSelectBrand(idx, id, found?.name || "")
                  }}
                  disabled={isChild || !canModify || !canModifyAppliance || !ap.appliancecategory?.id}
                  className={v('appliance_brand_id')}
                >
                  <option value="">{trans("random.select")}</option>
                  {brandOptions.map(b => (
                    <option key={"b-" + b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
                <input type="hidden" name={`appliances[${idx}][appliance_brand_name]`} value={ap.appliancebrand?.name || ""} />
              </div>
            </div>
            <div className="col">
              <div className="custom-input">
                <label>{trans("labels.model")}</label>
                <input
                  type="text"
                  name={`appliances[${idx}][appliance_model]`}
                  value={ap.appliancemodel?.name || ap.appliance_model || ""}
                  onChange={(e) => {
                    onModelChange(idx, "", e.target.value)
                  }}
                  disabled={isChild || !canModify || !canModifyAppliance}
                  className={v('appliance_model')}
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col">
              <label>{trans("labels.serialNo")}</label>
              <input
                name={`appliances[${idx}][serial_no]`}
                type="text"
                value={ap.serial_no || ""}
                onChange={(e) =>
                  onUpdateField(idx, "serial_no", e.target.value)
                }
                disabled={isChild || !canModify}
              />
            </div>
            <div className="col">
              <label>{trans("labels.productCode")}</label>
              <input
                name={`appliances[${idx}][product_code]`}
                type="text"
                value={ap.product_code || ""}
                onChange={(e) =>
                  onUpdateField(idx, "product_code", e.target.value)
                }
                disabled={isChild || !canModify}
              />
            </div>
          </div>

          <div className="row">
            <div className="col">
              <label>{trans("labels.problemDescription")}</label>
              <textarea
                name={`appliances[${idx}][description]`}
                value={ap.description || ""}
                onChange={(e) =>
                  onUpdateField(idx, "description", e.target.value)
                }
                disabled={isChild || !canModify}
              />
            </div>
          </div>

          <div className="row">
            <div className="col">
              <label>{trans("labels.employeeReport")}</label>
              <textarea
                name={`appliances[${idx}][report]`}
                value={ap.report || ""}
                onChange={(e) => onUpdateField(idx, "report", e.target.value)}
                disabled={!canModify}
              />
            </div>
          </div>
          <div className="upload-images">
            {images.length ? (
              <div className="images-grid">
                {images.map((image) => (
                  <div key={image.id} className="image-item">
                    <button
                      type="button"
                      className="delete-btn"
                      onClick={() => handleDeleteImage(image.id)}
                    >
                      <img src={deleteIcon} alt="Delete" />
                    </button>
                    <div className="image-preview">
                      <img
                        src={image.preview || image.image_path}
                        alt={image.name}
                      />
                    </div>
                    <div className="image-name">
                      {trans(image.uploaded_by ? `labels.${image.uploaded_by}` : 'labels.office')}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <></>
            )}

            {/* Hidden file input that stores actual binary files */}
            <input
              ref={hiddenFileInputRef}
              type="file"
              name={`appliances[${idx}][images][]`}
              multiple
              style={{ display: "none" }}
              disabled={isChild || !canModify}
            />

            {/* Visible file input for selecting files */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.png,.jpg,.jpeg"
              multiple
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />

            {imageSizeError && (
              <div className="alert danger" style={{ marginBottom: '10px' }}>
                {imageSizeError}
              </div>
            )}

            <div
              className="upload-btn"
              onClick={handleUploadClick}
              disabled={isChild || !canModify}
            >
              <img src={uploadImage} alt="Upload" />
              <div>
                <div className="upload-title">{trans('labels.attachImages')}</div>
                <div className="upload-subtitle">
                  {trans('labels.allowedImageFormats')}
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="row buttons-holder">
              <img src={loader} alt="loading" />
            </div>
          ) : (
            <></>
          )}
        </div>
    </div>
  )
}
